import {
    isPhoneNumberE164,
    ReferenceContactId,
    RegionId,
    TaskTypeId,
    toPhoneNumberE164,
} from "@sp-crm/core";
import { useQuery } from "@tanstack/react-query";
import { MessageCompose } from "components/messages/message-compose";
import { trpc } from "components/rpc/trpc";
import { VisitParent } from "components/visits/types";
import { AdvancedSearchEntityType } from "generated/graphql";
import React, { useState } from "react";
import { useFeature, useRegionId } from "store/selectors/hooks";
import { usePhoneLines } from "store/selectors/telecom";
import { handleEvent } from "util/user-events";
import { EntityMarketingPlan } from "./entity-marketing-plan";

interface ReferenceContactMarketingPlanProps {
    contactId: ReferenceContactId;
    variant: "marketingPlan" | "recommendedActions";
}

export const ReferenceContactMarketingPlan: React.FC<
    ReferenceContactMarketingPlanProps
> = ({ contactId, variant }) => {
    const marketingPlaybookEnabled = useFeature("marketingPlaybook");
    const regionId = useRegionId();
    const trpcClient = trpc.useTRPC();
    const phoneLines = usePhoneLines();
    const [composingActivityType, setComposingActivityType] = useState<{
        activityTypeId: TaskTypeId | null;
    } | null>(null);

    const planQuery = useQuery({
        ...trpcClient.marketingPlaybook.contactPlan.queryOptions({
            regionId: regionId ?? ("" as RegionId),
            contactId,
        }),
        enabled: marketingPlaybookEnabled && !!regionId,
    });

    if (!planQuery.isSuccess) {
        return null;
    }
    const plan = planQuery.data;

    const phone =
        plan?.contactPhone && isPhoneNumberE164(plan.contactPhone)
            ? toPhoneNumberE164(plan.contactPhone)
            : null;

    const parent: VisitParent | null = plan
        ? { kind: "contact", id: contactId, name: plan.contactName, address: null }
        : null;

    const openCompose = (activityTypeId: TaskTypeId | null) => {
        handleEvent("marketing-plan-send-email");
        setComposingActivityType({ activityTypeId });
    };

    return (
        <>
            <EntityMarketingPlan
                variant={variant}
                emptyStateNoun="contact"
                touchpoints={
                    plan?.touchpoints.map(touchpoint => ({
                        ...touchpoint,
                        lastContactedAt: touchpoint.lastContactedAt ?? null,
                        nextDueAt: touchpoint.nextDueAt ?? null,
                        pendingVisit: touchpoint.pendingVisit ?? null,
                    })) ?? null
                }
                parent={parent}
                email={plan?.contactEmail ?? null}
                emailOptedOut={plan?.contactEmailOptedOut ?? false}
                phone={phone}
                hasPhoneLine={phoneLines.length > 0}
                onSendEmail={openCompose}
            />
            {composingActivityType ? (
                <MessageCompose
                    entityId={contactId}
                    entityType={AdvancedSearchEntityType.ReferenceContact}
                    activityTypeId={composingActivityType.activityTypeId}
                    onDismiss={() => setComposingActivityType(null)}
                />
            ) : null}
        </>
    );
};
